/** The deepest block wrapper containing an element (or null). */
export const deepBlockIdFor = (el) =>
  el?.closest?.('[data-block-id]')?.getAttribute('data-block-id') || null;

/** True when every top-level block id is in `selectedIds` (order-insensitive). */
export const isAllTopLevelSelected = (tree, selectedIds) => {
  const topIds = (tree || []).map((b) => b.id);
  if (!topIds.length || !selectedIds?.length || selectedIds.length !== topIds.length) {
    return false;
  }
  const set = new Set(selectedIds);
  return topIds.every((id) => set.has(id));
};

const isEditableField = (el) => {
  if (!el) return false;
  if (el.tagName === 'TEXTAREA' || el.tagName === 'INPUT') return true;
  if (el.isContentEditable) return true;
  const flag = el.contentEditable;
  return flag === 'true' || flag === true || el.getAttribute?.('contenteditable') === 'true';
};

/** Nearest contenteditable / textarea for progressive Ctrl+A (focus or selection). */
export const editableFieldForSelectAll = (root, active = document.activeElement) => {
  if (!root) return null;
  if (active && root.contains(active) && isEditableField(active)) return active;

  const sel = window.getSelection();
  const node = sel?.anchorNode;
  if (!node) return null;
  const el = node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement;
  const field =
    el?.closest?.('textarea') ||
    el?.closest?.('[contenteditable="true"]') ||
    el?.closest?.('[contenteditable=""]');
  return field && root.contains(field) ? field : null;
};

/** Past this, Ctrl+A must not call innerText / Selection.toString (both force layout). */
export const HEAVY_FIELD_CHARS = 4000;
export const HEAVY_FIELD_NODES = 80;

const rangeCoversElement = (el, sel) => {
  try {
    const full = document.createRange();
    full.selectNodeContents(el);
    const range = sel.getRangeAt(0);
    return (
      range.compareBoundaryPoints(Range.START_TO_START, full) <= 0 &&
      range.compareBoundaryPoints(Range.END_TO_END, full) >= 0
    );
  } catch {
    return false;
  }
};

/**
 * Notion-style "entire field selected" check for progressive Ctrl+A.
 * Empty fields count as fully selected so the first press escalates to the block.
 */
export const isEntireFieldSelected = (el) => {
  if (!el || !isEditableField(el)) return false;
  // Value cells mark select-all without a DOM range (a real range over a
  // huge value lays out every line and freezes the tab).
  if (el.hasAttribute?.('data-value-all-selected')) return true;

  if (el.tagName === 'TEXTAREA' || el.tagName === 'INPUT') {
    const value = el.value ?? '';
    if (!value) return true;
    return el.selectionStart === 0 && el.selectionEnd === value.length;
  }

  const nodeHeavy = (el.childElementCount || 0) > HEAVY_FIELD_NODES;
  const text = nodeHeavy ? '' : (el.textContent || '').replace(/\u200B/g, '');
  const heavy = nodeHeavy || text.length > HEAVY_FIELD_CHARS;

  // innerText and Selection.toString() layout the whole field. Fine for a
  // sentence; multi-second on a large value cell.
  if (!heavy) {
    const raw = (el.innerText != null ? el.innerText : el.textContent) || '';
    const plain = raw.replace(/\u200B/g, '').replace(/\n$/, '');
    if (!plain.trim()) return true;

    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return false;
    if (!el.contains(sel.anchorNode) || !el.contains(sel.focusNode)) return false;

    const selected = sel.toString().replace(/\u200B/g, '').replace(/\n$/, '');
    if (selected === plain) return true;
    return rangeCoversElement(el, sel);
  }

  if (!text.replace(/\n$/, '').trim() && (el.childElementCount || 0) === 0) return true;

  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return false;
  if (!el.contains(sel.anchorNode) || !el.contains(sel.focusNode)) return false;
  return rangeCoversElement(el, sel);
};

/** Path of block ids from the root down to `id` (inclusive), or null. */
export const pathTo = (list, id, acc = []) => {
  for (const b of list) {
    if (b.id === id) return [...acc, b.id];
    if (b.children?.length) {
      const found = pathTo(b.children, id, [...acc, b.id]);
      if (found) return found;
    }
  }
  return null;
};

/**
 * Range of sibling ids between two blocks, taken at the level where their
 * paths diverge — so dragging across two paragraphs inside a callout selects
 * those paragraphs, while dragging out of the callout onto a top-level block
 * selects the whole callout and that block.
 */
export const rangeBetween = (tree, idA, idB) => {
  const pathA = pathTo(tree, idA);
  const pathB = pathTo(tree, idB);
  if (!pathA || !pathB) return null;

  let k = 0;
  while (pathA[k] && pathA[k] === pathB[k]) k += 1;
  // One block contains the other: select the outer (container) block
  if (!pathA[k] || !pathB[k]) return [pathA[Math.min(k, pathA.length - 1)]];

  // Walk down the shared ancestors to the sibling list they diverge in
  let list = tree;
  for (let i = 0; i < k; i += 1) {
    list = list.find((b) => b.id === pathA[i])?.children || [];
  }
  const ia = list.findIndex((b) => b.id === pathA[k]);
  const ib = list.findIndex((b) => b.id === pathB[k]);
  if (ia === -1 || ib === -1) return null;
  return list.slice(Math.min(ia, ib), Math.max(ia, ib) + 1).map((b) => b.id);
};
